import dayjs from 'dayjs';
import type { LiveLogEnvelope, LogEntry, LogHistoryPage } from '../types/logs.ts';

const DEFAULT_LEVEL = 'INFO';

function fallbackTimestamp(): string {
  return new Date().toISOString();
}

function normalizeLevel(value: unknown): string {
  const raw = String(value || DEFAULT_LEVEL).trim().toUpperCase();
  if (raw === 'WARNING') {
    return 'WARN';
  }
  return raw || DEFAULT_LEVEL;
}

function normalizeTimestamp(value: unknown): string {
  if (Array.isArray(value) && value.length >= 6) {
    const [year, month, day, hour, minute, second, nano] = value;
    const ms = typeof nano === 'number' ? Math.floor(nano / 1_000_000) : 0;
    return new Date(year, month - 1, day, hour, minute, second, ms).toISOString();
  }

  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = dayjs(value);
    if (parsed.isValid()) {
      return parsed.toISOString();
    }
    return value;
  }

  return fallbackTimestamp();
}

export function normalizeLogEntry(raw: any, fallbackId: string | number): LogEntry {
  return {
    id: raw?.id ?? fallbackId,
    level: normalizeLevel(raw?.level),
    message: String(raw?.message ?? ''),
    component: raw?.component ? String(raw.component) : '',
    source: raw?.source ? String(raw.source) : '',
    timestamp: normalizeTimestamp(raw?.timestamp),
  };
}

export function normalizeLogHistoryPage(raw: any): LogHistoryPage {
  const content = Array.isArray(raw?.content)
    ? raw.content.map((entry: any, index: number) => normalizeLogEntry(entry, `history-${raw?.number ?? 0}-${index}`))
    : [];

  return {
    content,
    last: Boolean(raw?.last ?? true),
    number: Number(raw?.number ?? 0),
    size: Number(raw?.size ?? content.length),
    totalElements: Number(raw?.totalElements ?? content.length),
  };
}

export function normalizeLiveLogEnvelope(envelope: LiveLogEnvelope, fallbackId: string): LogEntry {
  const payload = envelope?.data;

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return normalizeLogEntry(
      {
        ...payload,
        level: (payload as any).level ?? envelope?.message,
        timestamp: (payload as any).timestamp ?? envelope?.timestamp,
      },
      fallbackId
    );
  }

  return {
    id: fallbackId,
    level: normalizeLevel(envelope?.message),
    message: typeof payload === 'string' ? payload : String(envelope?.message ?? ''),
    component: '',
    source: 'LIVE',
    timestamp: normalizeTimestamp(envelope?.timestamp),
  };
}

export function matchesLogFilters(entry: LogEntry, filters: { level?: string; query?: string; from?: string; to?: string }): boolean {
  if (filters.level && normalizeLevel(filters.level) !== normalizeLevel(entry.level)) {
    return false;
  }

  const query = String(filters.query || '').trim().toLowerCase();
  if (query) {
    const haystack = [entry.message, entry.component, entry.source].join(' ').toLowerCase();
    if (!haystack.includes(query)) {
      return false;
    }
  }

  const ts = dayjs(entry.timestamp);
  if (filters.from) {
    const from = dayjs(filters.from);
    if (from.isValid() && ts.isBefore(from)) {
      return false;
    }
  }
  if (filters.to) {
    const to = dayjs(filters.to);
    if (to.isValid() && ts.isAfter(to)) {
      return false;
    }
  }

  return true;
}

export function formatLogTimestamp(value: string): string {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
}
